// FundInvestmentDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { 
    Building2, 
    DollarSign,
    Calculator,
    TrendingUp,
    Calendar,
    BarChart2,
    Clock
} from 'lucide-react';
import { useParams } from "react-router";
import './FundInvestmentDetailPage.css';
import { formatCurrency, formatDateIntl } from "../../util/currency_lookup";
import { LineChart, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader } from '../../components/loader';

const FundInvestmentDetailPage = () => {
  const [fund, setFund] = useState(null);
  const [investmentSummary, setInvestmentSummary] = useState(null);
  const [periodSummary, setPeriodSummary] = useState(null);
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [loading, setLoading] = useState({
    fund: true,
    summary: true,
    periodSummary: true,
    history: true
  });
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    fetchFund();
    fetchInvestmentSummary();
    fetchPeriodSummary();
    fetchInvestmentHistory();
  }, [id]);

  const fetchFund = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/by-id/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch fund');
      }

      const data = await response.json();
      setFund(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, fund: false }));
    }
  };

  const fetchInvestmentSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/investment/${id}/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investment summary');
      }

      const data = await response.json();
      setInvestmentSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  const fetchPeriodSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/investment/${id}/period`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch period summary');
      }

      const data = await response.json();
      setPeriodSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, periodSummary: false }));
    }
  };

  const fetchInvestmentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/investment/${id}/overtime`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investment history');
      }

      const data = await response.json();
      setInvestmentHistory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, history: false }));
    }
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) return <div className="error">{error}</div>;
  if (!fund) return <div className="error">No fund found</div>;

  return (
    <div className="fund-investment-detail-breakdown">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1>Fund Investment Details</h1>
          <div className="fund-name-investment">{fund.name} ({fund.symbol})</div>
        </div>
      </div>

      {/* Basic Fund Information */}
      <div className="overview">
        <div className="overview-card">
          <div className="overview-title">Fund Information</div>
          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-card-content">
                <Building2 className="detail-icon" />
                <div>
                  <div className="detail-label">Fund Name</div>
                  <div className="detail-value">{fund.name}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <DollarSign className="detail-icon" />
                <div>
                  <div className="detail-label">Currency</div>
                  <div className="detail-value">{fund.currency_code}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Calendar className="detail-icon" />
                <div>
                  <div className="detail-label">Created At</div>
                  <div className="detail-value">{formatDateIntl(fund.created_at)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Summary */}
      <div className="period-section">
        <h2>Investment Summary</h2>
        <div className="period-grid">
          <div className="period-card">
            <DollarSign className="period-icon" />
            <div className="period-content">
              <div className="period-label">Total Invested Amount</div>
              <div className="period-value">{formatCurrency(investmentSummary.total_net_investment, fund.currency_code)}</div>
            </div>
          </div>
          <div className="period-card">
            <BarChart2 className="summary-icon" />
            <div className="period-content">
              <div className="period-label">Number of Transactions</div>
              <div className="period-value">{investmentSummary.total_transactions}</div>
            </div>
          </div>
          <div className="period-card">
            <Calculator className="period-icon" />
            <div className="period-content">
              <div className="period-label">Average Invested Amount</div>
              <div className="period-value">{formatCurrency(investmentSummary.average_investment_size, fund.currency_code)}</div>
            </div>
          </div>
          <div className="period-card">
            <TrendingUp className="period-icon" />
            <div className="period-content">
              <div className="period-label">Largest Investment</div>
              <div className="period-value">{formatCurrency(investmentSummary.largest_investment, fund.currency_code)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Period Summary */}
      <div className="period-section">
        <h2>Investment Periods</h2>
        <div className="period-grid">
          <div className="period-card">
            <Clock className="period-icon" />
            <div className="period-content">
              <div className="period-label">Last 30 Days</div>
              <div className="period-value">{formatCurrency(periodSummary.days_30,  fund.currency_code)}</div>
            </div>
          </div>
          <div className="period-card">
            <Clock className="period-icon" />
            <div className="period-content">
              <div className="period-label">Last 60 Days</div>
              <div className="period-value">{formatCurrency(periodSummary.days_60,  fund.currency_code)}</div>
            </div>
          </div>
          <div className="period-card">
            <Clock className="period-icon" />
            <div className="period-content">
              <div className="period-label">Last 90 Days</div>
              <div className="period-value">{formatCurrency(periodSummary.days_90,  fund.currency_code)}</div>
            </div>
          </div>
          <div className="period-card">
            <Clock className="period-icon" />
            <div className="period-content">
              <div className="period-label">Last 120 Days</div>
              <div className="period-value">{formatCurrency(periodSummary.days_120,  fund.currency_code)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment History Chart */}
      <div className="chart-section">
        <h2>Investment History</h2>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={investmentHistory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => formatDateIntl(date, 'short')}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value, fund.currency_code, true)}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value, fund.currency_code)}
                labelFormatter={(date) => formatDateIntl(date)}
              />
            <Bar dataKey="new_investments" fill="#4ade80" name="New Investments" />
            <Bar dataKey="redemptions" fill="#f87171" name="Redemptions" />
              <Line 
                type="monotone" 
                dataKey="total_investment" 
                stroke="#6366f1" 
                name="Total Investment"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default FundInvestmentDetailPage;
